import { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import WarningIcon from "@mui/icons-material/WarningAmber";
import { useAlert } from "../../functions/useAlert";
import { updateContactGraph } from "../../functions/knowledgeGraphApi";
import { fetchAllContacts } from "../../services/contacts.service";
import { linkContact } from "../../services/relationships.service";
import { getContactConfig } from "../../utils/contactConfig";
import { Client, Contact } from "./types";

interface ClientLinkContactModalProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  client: Client | null;
  orgId: string;
  userId: string;
  existingLinkedContacts?: Contact[];
  onCreateNew: () => void;
  preSelectedContact?: any;
}

const ClientLinkContactModal = ({
  open,
  onClose,
  onSave,
  client,
  orgId,
  userId,
  existingLinkedContacts = [],
  onCreateNew,
  preSelectedContact = null,
}: ClientLinkContactModalProps) => {
  const [allContacts, setAllContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState<any>(null);
  const [relationshipForm, setRelationshipForm] = useState({
    relationshipType: "",
    isPrimary: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [setAlert] = useAlert();

  const [primaryConfirmOpen, setPrimaryConfirmOpen] = useState(false);
  const [existingPrimaryName, setExistingPrimaryName] = useState("");

  const relationshipOptions = getContactConfig(client?.entityType).relationships;

  useEffect(() => {
    if (open) {
      const loadAllContacts = async () => {
        try {
          const response: any = await fetchAllContacts();
          const fetchedContacts = response.contacts || [];

          const sortedContacts = [...fetchedContacts].sort((a, b) => {
            return (
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
            );
          });

          setAllContacts(sortedContacts);
          if (!preSelectedContact) {
            // AUTO-SET isPrimary to true if this is the first contact being linked
            const isFirstContact = existingLinkedContacts.length === 0;
            setRelationshipForm({
              relationshipType: "",
              isPrimary: isFirstContact,
            });
          }
          // Auto-select the newly created contact if provided
          if (preSelectedContact) {
            // Find the contact in the fetched list by ID or email
            const matchedContact = sortedContacts.find(
              (c: any) =>
                c._id === preSelectedContact._id ||
                c.email === preSelectedContact.email,
            );
            if (matchedContact) {
              setSelectedContact(matchedContact);
            } else {
              // Not found in org list — use the object passed in directly
              setSelectedContact(preSelectedContact);
            }
          } else {
            setSelectedContact(null);
          }
        } catch (error) {
          console.error("Failed to fetch contacts list:", error);
        }
      };

      if (!preSelectedContact) {
        setRelationshipForm({ relationshipType: "", isPrimary: false });
      }

      loadAllContacts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, preSelectedContact]);

  const availableContacts = useMemo(() => {
    const linkedContactIds = new Set(existingLinkedContacts.map((c) => c._id));
    let filtered = allContacts.filter(
      (contact) => !linkedContactIds.has(contact._id),
    );
    if (
      preSelectedContact &&
      !filtered.find((c: any) => c._id === preSelectedContact._id)
    ) {
      filtered = [preSelectedContact, ...filtered];
    }
    return filtered;
  }, [allContacts, existingLinkedContacts, preSelectedContact]);

  // ── Actual API call — accepts forcePrimary ────────────────────────────────
  const doLink = async (forcePrimary: boolean) => {
    setIsLoading(true);
    try {
      const payload = {
        clientId: client._id,
        contactId: selectedContact._id,
        relationshipType: relationshipForm.relationshipType,
        isPrimary: relationshipForm.isPrimary,
        orgId,
        userId,
        forcePrimary,
      };

      const response: any = await linkContact(payload);

      if (response.error) throw new Error(response.error);

      setAlert("Contact linked successfully!", "success");
      updateContactGraph({ ...payload, _id: payload.contactId }).catch(
        () => { },
      );
      window.dispatchEvent(new Event("contactsUpdated"));
      onSave();
      onClose();
    } catch (error: any) {
      setAlert(error.message || "Failed to link contact.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  // ── On "Link Contact" click: check frontend first, skip API if conflict ───
  const handleLinkContact = () => {
    if (!selectedContact || !relationshipForm.relationshipType) return;

    if (relationshipForm.isPrimary) {
      const currentPrimary = existingLinkedContacts.find(
        (c: any) => c.isPrimary,
      );
      if (currentPrimary) {
        // Show confirmation dialog — do NOT call API yet
        setExistingPrimaryName(currentPrimary.name || "Existing Contact");
        setPrimaryConfirmOpen(true);
        return;
      }
    }

    doLink(false);
  };

  const handleConfirmSwap = () => {
    setPrimaryConfirmOpen(false);
    doLink(true); // forcePrimary=true → backend unsets old primary
  };

  const handleCancelSwap = () => {
    setPrimaryConfirmOpen(false);
    setExistingPrimaryName("");
    // Uncheck the toggle since user said no
    setRelationshipForm((prev) => ({ ...prev, isPrimary: false }));
  };

  return (
    <>
      {/* ── Main link modal ── */}
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: "12px" } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
            fontWeight: 600,
            fontSize: "1.25rem",
          }}
        >
          Link Contact to {client?.name}
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Box>
            <Stack spacing={3}>
              <Typography variant="body2" color="text.secondary">
                Search for an existing contact to link, or create a new one.
              </Typography>

              <Box display="flex" gap={1} alignItems="center">
                <Autocomplete
                  fullWidth
                  options={availableContacts}
                  getOptionLabel={(option: any) =>
                    `${option.name} (${option.email})` || ""
                  }
                  value={selectedContact}
                  onChange={(_, newValue) => setSelectedContact(newValue)}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Search for a contact" size="small" />
                  )}
                />
                <Button
                  variant="outlined"
                  onClick={onCreateNew}
                  sx={{
                    minWidth: "130px",
                    height: "40px",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                  }}
                  startIcon={<AddIcon />}
                >
                  Create New
                </Button>
              </Box>

              <Collapse in={!!selectedContact}>
                <Paper
                  variant="outlined"
                  sx={{ p: 2, bgcolor: "background.default" }}
                >
                  <Typography variant="subtitle2" gutterBottom>
                    Define relationship for{" "}
                    <strong>{selectedContact?.name}</strong>:
                  </Typography>
                  <Stack spacing={2} pt={1}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Relationship Type</InputLabel>
                      <Select
                        value={relationshipForm.relationshipType}
                        label="Relationship Type"
                        onChange={(e) =>
                          setRelationshipForm({
                            ...relationshipForm,
                            relationshipType: e.target.value,
                          })
                        }
                      >
                        {relationshipOptions.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={relationshipForm.isPrimary}
                          onChange={(e) =>
                            setRelationshipForm({
                              ...relationshipForm,
                              isPrimary: e.target.checked,
                            })
                          }
                        />
                      }
                      label="Set as Primary Contact"
                    />
                  </Stack>
                </Paper>
              </Collapse>
            </Stack>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={onClose} variant="outlined" color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleLinkContact}
            variant="contained"
            disabled={
              !selectedContact ||
              !relationshipForm.relationshipType ||
              isLoading
            }
          >
            {isLoading ? <CircularProgress size={24} /> : "Link Contact"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Primary contact swap confirmation ── */}
      <Dialog
        open={primaryConfirmOpen}
        onClose={handleCancelSwap}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px" } }}
      >
        <DialogTitle
          sx={{ display: "flex", alignItems: "center", gap: 1, pb: 1 }}
        >
          <WarningIcon sx={{ color: "warning.main" }} />
          Change Primary Contact?
        </DialogTitle>
        <DialogContent>
          <Box>
            <Typography variant="body2" color="text.primary">
              The current primary contact for this client is{" "}
              <strong>"{existingPrimaryName}"</strong>. Would you like to set{" "}
              <strong>"{selectedContact?.name}"</strong> as the new primary
              contact instead?
            </Typography>
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 1.5 }}
          >
            The existing primary contact will be unset automatically.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 1 }}>
          <Button onClick={handleCancelSwap} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmSwap}
            variant="contained"
            disabled={isLoading}
          >
            {isLoading ? <CircularProgress size={20} /> : "Confirm Change"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ClientLinkContactModal;
